import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import clsx from 'clsx';
import { computeResponsiveness } from '@picsart/ds-foundation';
import { IconLoading } from '@picsart/ds-foundation/Icons/internal';
import { ButtonSizeXL } from '../sizes/index.js';
import { useSkinStyles, useLayoutStyles } from '../styles/index.js';
import { ButtonElementTypes, ButtonVariants } from '../types/index.js';
import { ButtonSkinPrimary } from '../skins/index.js';

const Button = forwardRef(({ variant = ButtonVariants.Filled, tag: Tag = ButtonElementTypes.Button, skin = ButtonSkinPrimary, size = ButtonSizeXL, className, children, startIcon: StartIcon, endIcon: EndIcon, centerIcon: CenterIcon, isFullWidth, isActive, isDisabled, isLoading, dataTestId, isSubmitButton = false, ...props }, ref) => {
    const skinStyles = useSkinStyles({
        skin, Tag, variant, isLoading,
    });
    const sizes = computeResponsiveness(size);
    const layoutStyles = useLayoutStyles({
        variant,
        Tag,
        centerIcon: CenterIcon,
        sizes,
        isFullWidth,
        isDisabled,
        isLoading,
    });
    return (jsxs(Tag, { ...(Tag !== ButtonElementTypes.Upload && { ...props, ref }), ...(Tag === ButtonElementTypes.Button && { type: isSubmitButton ? 'submit' : 'button' }), className: clsx(layoutStyles.root, {
            [skinStyles.default]: !isActive,
            [skinStyles.interactive]: !isActive && !isDisabled && !isLoading,
            [skinStyles.active]: isActive,
            [skinStyles.activeInteraction]: isActive && !isDisabled,
            [skinStyles.disabled]: isDisabled,
            [layoutStyles.loading]: !isActive && isLoading,
        }, className), ...(Tag === ButtonElementTypes.Button && { disabled: isDisabled }), "data-testid": dataTestId || props['data-testid'] || 'button', children: [Tag === ButtonElementTypes.Upload && (jsx("input", { type: "file", className: layoutStyles.control, disabled: isDisabled, ref: ref, ...props, "data-testid": "button-input" })), StartIcon && !CenterIcon && !isLoading && jsx(StartIcon, { className: clsx(layoutStyles.icon), "data-testid": "start-icon" }), !isLoading && CenterIcon && jsx(CenterIcon, { className: layoutStyles.icon, "data-testid": "center-icon" }), children ? jsx("span", { className: layoutStyles.content, children: children }) : null, EndIcon && !CenterIcon && !isLoading && jsx(EndIcon, { className: clsx(layoutStyles.icon), "data-testid": "end-icon" }), isLoading && jsx(IconLoading, { className: clsx(layoutStyles.icon, layoutStyles.loadingIcon, skinStyles.loadingIcon), "data-testid": "loading-icon" })] }));
});
Button.displayName = 'Button';

export { Button };
